import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../lib/auth.tsx';
import { useInactivityTimeout } from '../hooks/useInactivityTimeout.ts';
import { SessionTimeoutWarning } from './SessionTimeoutWarning.tsx';
import { supabase } from '../lib/db.ts';
import { logger } from '../services/logger.ts';
import { useLocation } from 'react-router-dom';

interface SessionTimeoutProviderProps {
  children: React.ReactNode;
}

interface TimeoutSettings {
  inactivity_timeout_minutes: number;
  warning_time_minutes: number;
  financial_ops_timeout_minutes: number;
  enabled: boolean;
}

// Default timeout settings (in case DB settings aren't available)
const DEFAULT_SETTINGS: TimeoutSettings = {
  inactivity_timeout_minutes: 20,
  warning_time_minutes: 3,
  financial_ops_timeout_minutes: 10,
  enabled: true,
};

const normalizeTimeoutValue = (
  rawValue: unknown,
  fallback: number,
  min: number,
  max: number
): number => {
  const parsed = Number.parseInt(String(rawValue), 10);
  if (!Number.isFinite(parsed)) {
    return fallback;
  }

  return Math.min(Math.max(parsed, min), max);
};

/**
 * Provider component that wraps the app and handles session timeout logic
 * Uses different timeout periods for financial operations vs regular browsing
 */
export function SessionTimeoutProvider({ children }: SessionTimeoutProviderProps) {
  const { user, signOut } = useAuth();
  const location = useLocation();
  const [settings, setSettings] = useState<TimeoutSettings>(DEFAULT_SETTINGS);
  const [isLoadingSettings, setIsLoadingSettings] = useState(true);

  // Check if user is on a financial operations page
  const isFinancialPage = useCallback(() => {
    const financialPaths = [
      '/wallet',
      '/deposit',
      '/withdraw',
      '/admin/wallet',
      '/admin/transactions',
    ];
    return financialPaths.some(path => location.pathname.includes(path));
  }, [location.pathname]);

  // Load timeout settings from database
  useEffect(() => {
    const loadSettings = async () => {
      try {
        const { data, error } = await supabase
          .from('system_settings')
          .select('key, value')
          .in('key', [
            'inactivity_timeout_minutes',
            'warning_time_minutes',
            'financial_ops_timeout_minutes',
            'session_timeout_enabled',
          ]);

        if (error) {
          logger.error('Error loading timeout settings:', error);
          setSettings(DEFAULT_SETTINGS);
        } else if (data && data.length > 0) {
          const loadedSettings: Partial<TimeoutSettings> = {};
          
          data.forEach((row: { key: string; value: unknown }) => {
            // Extract value from JSONB
            const value = typeof row.value === 'string' ? row.value : String(row.value);
            
            if (row.key === 'inactivity_timeout_minutes') {
              loadedSettings.inactivity_timeout_minutes = normalizeTimeoutValue(
                value,
                DEFAULT_SETTINGS.inactivity_timeout_minutes,
                5,
                120
              );
            } else if (row.key === 'warning_time_minutes') {
              loadedSettings.warning_time_minutes = normalizeTimeoutValue(
                value,
                DEFAULT_SETTINGS.warning_time_minutes,
                1,
                10
              );
            } else if (row.key === 'financial_ops_timeout_minutes') {
              loadedSettings.financial_ops_timeout_minutes = normalizeTimeoutValue(
                value,
                DEFAULT_SETTINGS.financial_ops_timeout_minutes,
                5,
                60
              );
            } else if (row.key === 'session_timeout_enabled') {
              loadedSettings.enabled = value === 'true' || value === true;
            }
          });

          setSettings({
            ...DEFAULT_SETTINGS,
            ...loadedSettings,
          });

          logger.info('Loaded session timeout settings', loadedSettings);
        } else {
          setSettings(DEFAULT_SETTINGS);
        }
      } catch (err) {
        logger.error('Exception loading timeout settings:', err);
        setSettings(DEFAULT_SETTINGS);
      } finally {
        setIsLoadingSettings(false);
      }
    };

    loadSettings();
  }, []);

  // Handle timeout
  const handleTimeout = useCallback(async () => {
    logger.warn('Session timeout reached, logging out user', {
      userId: user?.id,
      email: user?.email,
      pathname: location.pathname,
      isFinancialPage: isFinancialPage(),
    });

    try {
      await signOut();
      // The signOut function will handle redirect to login
    } catch (error) {
      logger.error('Error during timeout logout:', error);
      // Force logout even if error occurs
      window.location.href = '/login';
    }
  }, [user, signOut, location.pathname, isFinancialPage]);

  // Determine timeout based on current page
  const getTimeoutConfig = useCallback(() => {
    const isFinancial = isFinancialPage();
    const timeoutMinutes = isFinancial 
      ? settings.financial_ops_timeout_minutes 
      : settings.inactivity_timeout_minutes;

    return {
      inactivityTimeout: timeoutMinutes * 60 * 1000,
      warningTime: settings.warning_time_minutes * 60 * 1000,
      checkInterval: 1000,
    };
  }, [settings, isFinancialPage]);

  // Use the inactivity timeout hook
  const {
    isWarningVisible,
    timeRemaining,
    extendSession,
  } = useInactivityTimeout(
    getTimeoutConfig(),
    handleTimeout,
    settings.enabled && !!user && !isLoadingSettings
  );

  // Handle "Stay Logged In" button
  const handleExtendSession = useCallback(() => {
    extendSession();
    logger.info('User extended session', {
      userId: user?.id,
      pathname: location.pathname,
    });
  }, [extendSession, user, location.pathname]);

  // Handle "Logout Now" button
  const handleLogoutNow = useCallback(async () => {
    logger.info('User manually logged out from timeout warning', {
      userId: user?.id,
    });
    await signOut();
  }, [user, signOut]);

  return (
    <>
      {children}
      <SessionTimeoutWarning
        isVisible={isWarningVisible}
        timeRemaining={timeRemaining}
        onExtendSession={handleExtendSession}
        onLogoutNow={handleLogoutNow}
      />
    </>
  );
}

